import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { ThemePreference } from "@/shared/theme/ThemeContext";
import { useTheme } from "@/shared/theme/useTheme";

import { SettingsSection } from "./SettingsSection";

type ThemeOption = ThemePreference;

interface ThemeCardProps {
  id: ThemeOption;
  label: string;
  selected: boolean;
  onClick: () => void;
  hint: string;
  badge?: string;
}

function PreviewSurface({ dark }: { dark: boolean }) {
  const bg = dark ? "#1c1917" : "#fafaf9";
  const sf = dark ? "#292524" : "#ffffff";
  const sf2 = dark ? "#1f1d1b" : "#f5f5f4";
  const bd = dark ? "#3a3633" : "#e7e5e4";
  const tx = dark ? "#f5f5f4" : "#1c1917";
  const tx2 = dark ? "#a8a29e" : "#78716c";
  const pine = "#00735C";
  return (
    <div className="flex h-full" style={{ background: bg }}>
      <div className="flex w-[22px] flex-col gap-1 border-r p-1" style={{ background: sf2, borderColor: bd }}>
        <div className="h-1.5 w-3.5 rounded-sm" style={{ background: pine }} />
        <div className="h-[3px] w-3.5 rounded-[1px]" style={{ background: bd }} />
        <div className="h-[3px] w-3.5 rounded-[1px]" style={{ background: bd }} />
        <div className="h-[3px] w-3.5 rounded-[1px]" style={{ background: bd }} />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-1.5">
        <div className="h-2 w-1/2 rounded-sm opacity-90" style={{ background: tx }} />
        <div className="h-[5px] w-[70%] rounded-[1px]" style={{ background: tx2 }} />
        <div
          className="mt-0.5 flex flex-1 flex-col gap-[3px] rounded-[3px] border p-1"
          style={{ background: sf, borderColor: bd }}
        >
          <div className="h-[4px] w-2/5 rounded-[1px]" style={{ background: tx2 }} />
          <div className="mt-0.5 h-2.5 w-[65%] rounded-sm" style={{ background: pine }} />
          <div className="mt-auto flex gap-[3px]">
            <div
              className="h-[5px] flex-1 rounded-[1px] border"
              style={{ background: sf2, borderColor: bd }}
            />
            <div className="h-[5px] flex-1 rounded-[1px]" style={{ background: pine }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewMini({ variant }: { variant: ThemeOption }) {
  if (variant === "system") {
    return (
      <div className="grid h-full grid-cols-2">
        <div className="border-r border-border">
          <PreviewSurface dark={false} />
        </div>
        <PreviewSurface dark={true} />
      </div>
    );
  }
  return <PreviewSurface dark={variant === "dark"} />;
}

function ThemeCard({ id, label, selected, onClick, hint, badge }: ThemeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative cursor-pointer overflow-hidden rounded-md border bg-background p-0 text-left transition-colors",
        selected
          ? "border-primary shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_12%,transparent)]"
          : "border-border hover:border-foreground/20",
      )}
    >
      <div className="h-[110px] border-b border-border">
        <PreviewMini variant={id} />
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span
          className={cn(
            "grid h-3.5 w-3.5 place-items-center rounded-full border-[1.5px]",
            selected ? "border-primary bg-primary" : "border-foreground/40",
          )}
        >
          {selected && <span className="h-1 w-1 rounded-full bg-white" />}
        </span>
        <span className="text-[13px] font-semibold">{label}</span>
        {badge && (
          <span className="ml-auto rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
            {badge}
          </span>
        )}
      </div>
      {hint && <div className="px-3 pb-3 text-[11.5px] text-muted-foreground">{hint}</div>}
    </button>
  );
}

export function AppearanceSection() {
  const { t } = useTranslations("settings");
  const { preference, setPreference } = useTheme();

  return (
    <SettingsSection
      id="apariencia"
      eyebrow={t("appearance_section_title")}
      title={t("appearance_theme_card_title")}
      sub={t("appearance_theme_card_sub")}
    >
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
        <ThemeCard
          id="light"
          label={t("theme_light")}
          selected={preference === "light"}
          onClick={() => setPreference("light")}
          hint={t("appearance_theme_light_hint")}
        />
        <ThemeCard
          id="dark"
          label={t("theme_dark")}
          selected={preference === "dark"}
          onClick={() => setPreference("dark")}
          hint={t("appearance_theme_dark_hint")}
        />
        <ThemeCard
          id="system"
          label={t("appearance_theme_system_label")}
          selected={preference === "system"}
          onClick={() => setPreference("system")}
          hint={t("appearance_theme_system_hint")}
          badge="AUTO"
        />
      </div>
    </SettingsSection>
  );
}
